import type { AllTableCriteria } from '@openx/types/tableCriteria';
import { type MutableRefObject, useCallback, useMemo } from 'react';
import { promptModes } from '../constants';
import { areFiltersDifferent } from '../utils/compareWisspAndUserFilters';
import { useDealPromptDomainsContext } from '../utils/context/DealPromptDomainsContext';
import { criteriaHasSelectedFilters } from '../utils/criteriaHasSelectedFilters';
import {
  mapAudienceCriteriaToFiltersBody,
  mapAudienceSearchFiltersToCriteria,
  mapFiltersBodyToCriteria,
} from '../utils/filters/filterMappers';
import { sanitizeDealPromptFormatAndDevices } from '../utils/filters/sanitizeDealPromptFormatAndDevices';
import { mapTagGroups } from '../utils/mapTagGroups';
import {
  type DealPromptContextType,
  type DealPromptFilters,
  dealPromptContextTypes,
  dealPromptFilterSegmentSearchMode,
} from '../utils/types';
import { useAudienceSearchFetch } from './useAudienceSearchFetch';
import { useAudienceSearchTextFetch } from './useAudienceSearchTextFetch';
import { useDealPrompt } from './useDealPrompt';
import { useDealPromptPermission } from './useDealPromptPermission';
import { useFetchResults } from './useFetchResults';

export const getAudienceDefaultFilters = (isInternalUser: boolean): DealPromptFilters => {
  if (isInternalUser) {
    return {
      segment_search_mode: dealPromptFilterSegmentSearchMode.OXS_DATA_MARKETPLACE,
      region: 'US',
    };
  }

  return {
    region: 'US',
  };
};

type UseDealPromptMethodsArgs = {
  abortControllerRef: MutableRefObject<AbortController | null>;
  criteria: AllTableCriteria<DealPromptFilters>;
  handleFiltersChange: (value: DealPromptFilters) => void;
};

export const useDealPromptMethods = ({
  abortControllerRef,
  criteria,
  handleFiltersChange,
}: UseDealPromptMethodsArgs) => {
  const {
    setNumberOfResults,
    setCumulativeData,
    setAudienceSegments,
    setAudienceSegmentsSets,
    setGeneratedSearchInput,
    generatedSearchInput,
  } = useDealPromptDomainsContext();
  const {
    state,
    setLoading,
    setRequestAborted,
    setFirstLoadPerformed,
    setLastSubmittedPrompt,
    setLastSubmittedQueries,
    setLastSubmittedFilterCriteria,
    setLastSubmittedContextType,
    setMode,
    setContextType: updateContextType,
    setPrompt: updatePrompt,
    setHasError,
    setHasDifferentFiltersThanWissp,
  } = useDealPrompt();

  const { prompt, contextType, loading, mode, lastSubmittedPrompt, lastSubmittedQueries, lastSubmittedContextType } =
    state;
  const { isInternalUser } = useDealPromptPermission();

  const promptChanged = useMemo(
    () => prompt !== '' && (prompt !== lastSubmittedPrompt || contextType !== lastSubmittedContextType),
    [prompt, lastSubmittedPrompt, contextType, lastSubmittedContextType]
  );

  const mappedTagGroups = useMemo(
    () => mapTagGroups(generatedSearchInput?.tagGroups),
    [generatedSearchInput?.tagGroups]
  );

  const fetchResults = useFetchResults(contextType, criteria, promptChanged);
  const fetchAudienceSearchText = useAudienceSearchTextFetch(criteria);
  const fetchAudienceSearch = useAudienceSearchFetch({
    filters: mapAudienceCriteriaToFiltersBody(criteria),
    tagGroups: mappedTagGroups,
  });

  const setPrompt = useCallback((value: string) => updatePrompt(value), [updatePrompt]);

  const getResults = useCallback(
    async (searchOnly?: boolean) => {
      const abortController = new AbortController();
      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = abortController;

      setLoading(true);
      setRequestAborted(false);
      setHasError(false);

      let lastSubmittedPrompt = prompt;
      let lastSubmittedCriteria = criteria;

      if (contextType === dealPromptContextTypes.AUDIENCE) {
        if (searchOnly) {
          const { segments, segmentsSets, _error } = await fetchAudienceSearch(abortControllerRef.current.signal);
          setAudienceSegments(segments);
          setAudienceSegmentsSets(segmentsSets);
          setHasError(!!_error);
        } else {
          const { combinedResult, generatedSearchInput, _error } = await fetchAudienceSearchText(
            { query: prompt },
            abortControllerRef.current.signal
          );
          setGeneratedSearchInput(generatedSearchInput);
          setAudienceSegments(combinedResult.segments);
          setAudienceSegmentsSets(combinedResult.segmentsSets);
          setHasError(!!_error);

          if (!_error && generatedSearchInput?.filters) {
            const mappedAudienceFilters = mapAudienceSearchFiltersToCriteria(generatedSearchInput.filters);
            const mergedFilters: DealPromptFilters = {
              ...(criteria.filters ?? {}),
              ...mappedAudienceFilters,
            };
            const normalizedMergedFilters = sanitizeDealPromptFormatAndDevices(mergedFilters, criteria.filters);
            handleFiltersChange(mergedFilters);
            lastSubmittedCriteria = { filters: normalizedMergedFilters };
          }
        }

        if (!abortControllerRef.current?.signal.aborted) {
          setFirstLoadPerformed(true);
          setLastSubmittedPrompt(lastSubmittedPrompt);
          setLastSubmittedFilterCriteria(lastSubmittedCriteria);
          setLastSubmittedContextType(contextType);
          setMode(promptModes.PREVIEW_MODE);
        }
        setLoading(false);
        abortControllerRef.current = null;
        return;
      }

      // Handle other context types
      if (!fetchResults) {
        setLoading(false);
        abortControllerRef.current = null;
        return;
      }

      // The NLP search runs with sub-queries derived from the prompt, not the prompt itself.
      // Reuse them while the prompt is unchanged so filter-only searches query the same topics.
      let submittedQueries = promptChanged || !prompt ? [] : lastSubmittedQueries;

      const { cumulativeData, numberOfResults, _error, inputs } = await fetchResults(
        { query: prompt, queries: submittedQueries },
        abortControllerRef.current.signal
      );
      setNumberOfResults(numberOfResults);
      setCumulativeData(cumulativeData);
      setHasError(!!_error);

      if (inputs) {
        submittedQueries =
          inputs.wissp?.query?.map(item => item?.query).filter((query): query is string => !!query) ?? [];

        if (inputs.user && inputs.wissp) {
          const hasDifferentFiltersThanWissp = areFiltersDifferent(inputs.user, inputs.wissp);
          setHasDifferentFiltersThanWissp(hasDifferentFiltersThanWissp);
        }

        if (inputs.combined) {
          const filters = mapFiltersBodyToCriteria(inputs.combined.filters, contextType);
          handleFiltersChange(filters);
          lastSubmittedCriteria = { filters };

          const returnedPrompt = inputs.combined.query[0].query;
          if (returnedPrompt !== prompt) {
            setPrompt(returnedPrompt);
            lastSubmittedPrompt = returnedPrompt;
          }
        }
      }

      if (!abortControllerRef.current?.signal.aborted) {
        setFirstLoadPerformed(true);
        setLastSubmittedPrompt(lastSubmittedPrompt);
        setLastSubmittedQueries(submittedQueries);
        setLastSubmittedFilterCriteria(lastSubmittedCriteria);
        setLastSubmittedContextType(contextType);
        setMode(promptModes.PREVIEW_MODE);
      }

      setLoading(false);
      abortControllerRef.current = null;
    },
    [
      fetchResults,
      setPrompt,
      prompt,
      promptChanged,
      lastSubmittedQueries,
      contextType,
      setNumberOfResults,
      setCumulativeData,
      setLoading,
      setRequestAborted,
      setFirstLoadPerformed,
      setLastSubmittedPrompt,
      setLastSubmittedQueries,
      setLastSubmittedFilterCriteria,
      setLastSubmittedContextType,
      setMode,
      abortControllerRef,
      criteria,
      fetchAudienceSearchText,
      fetchAudienceSearch,
      setAudienceSegments,
      setAudienceSegmentsSets,
      setGeneratedSearchInput,
      setHasError,
      handleFiltersChange,
      setHasDifferentFiltersThanWissp,
    ]
  );

  const hasAnyFilter = useMemo(() => criteriaHasSelectedFilters(criteria.filters), [criteria?.filters]);

  const handleSubmit = useCallback(
    (searchOnly?: boolean) => {
      if ((!prompt.trim() && !hasAnyFilter) || loading) return;
      getResults(searchOnly);
    },
    [prompt, loading, hasAnyFilter, getResults]
  );

  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setRequestAborted(true);
    }
  }, [abortControllerRef.current, setRequestAborted]);

  const handleModeChange = useCallback(
    (nextMode: typeof promptModes.EDIT_MODE | typeof promptModes.PREVIEW_MODE) => setMode(nextMode),
    [setMode]
  );

  const setContextType = useCallback(
    (value: DealPromptContextType) => {
      setAudienceSegments(null);
      setAudienceSegmentsSets(null);
      if (value === dealPromptContextTypes.AUDIENCE) {
        handleFiltersChange(getAudienceDefaultFilters(isInternalUser));
      } else {
        handleFiltersChange({});
      }
      updatePrompt('');
      updateContextType(value);
      if (mode === promptModes.PREVIEW_MODE) {
        setMode(promptModes.DEFAULT_MODE);
      }
      setFirstLoadPerformed(false);
      setCumulativeData(null);
      setNumberOfResults(0);
      setHasError(false);
    },
    [
      handleFiltersChange,
      mode,
      setNumberOfResults,
      setCumulativeData,
      updatePrompt,
      updateContextType,
      setMode,
      setFirstLoadPerformed,
      setAudienceSegments,
      setAudienceSegmentsSets,
      isInternalUser,
      setHasError,
    ]
  );

  const resetPrompt = useCallback(() => {
    setMode(promptModes.DEFAULT_MODE);
    setPrompt('');
    setCumulativeData(null);
    setAudienceSegments(null);
    setNumberOfResults(null);
    setFirstLoadPerformed(false);
    setHasDifferentFiltersThanWissp(false);
    setLastSubmittedPrompt('');
    setLastSubmittedQueries([]);
    if (contextType === dealPromptContextTypes.AUDIENCE) {
      handleFiltersChange(getAudienceDefaultFilters(isInternalUser));
    } else {
      handleFiltersChange({});
    }
    setLastSubmittedFilterCriteria({ filters: {} });
    setRequestAborted(false);
    setHasError(false);
  }, [
    handleFiltersChange,
    setAudienceSegments,
    setCumulativeData,
    setMode,
    setNumberOfResults,
    setPrompt,
    setFirstLoadPerformed,
    setHasDifferentFiltersThanWissp,
    setLastSubmittedPrompt,
    setLastSubmittedQueries,
    setLastSubmittedFilterCriteria,
    setRequestAborted,
    setHasError,
    contextType,
    isInternalUser,
  ]);

  return {
    handleSubmit,
    handleCancel,
    handleModeChange,
    setPrompt,
    setContextType,
    resetPrompt,
  };
};
